import { useSeo } from "@/lib/seo";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, X, Truck, Hand, MapPin, Loader2, WalletCards, Award } from "lucide-react";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { getCachedCoords, requestCoords } from "@/lib/geo";
import { toast } from "sonner";
import { TRADING_CARDS_CATEGORY, ALL_CARD_GAMES, CARD_TYPES } from "@/lib/cards";

const CATEGORIES = ["All", TRADING_CARDS_CATEGORY, "Cameras", "Phones", "Computers", "Tablets", "Gaming", "Music", "Clothing", "Home", "Furniture", "Sports", "Electronics", "Books", "Jewelry & Watches"];
const DELIVERY_OPTIONS = [
  { value: "", label: "Any", icon: null },
  { value: "local", label: "Local", icon: Hand },
  { value: "shipping", label: "Shipping", icon: Truck },
];
const RADIUS_OPTIONS = [
  { value: 0,   label: "Nationwide" },
  { value: 10,  label: "Local (10 mi)" },
  { value: 30,  label: "30 mi" },
  { value: 50,  label: "50 mi" },
  { value: 100, label: "100 mi" },
];

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  useSeo({
    title: "Browse Items to Trade | Vyrti Barter Marketplace",
    description: "Explore items offered for trade near you — electronics, cameras, fashion, music gear, furniture and more. No money, just trades.",
    path: "/browse",
  });
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [delivery, setDelivery] = useState(searchParams.get("delivery") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "new");
  const [radius, setRadius] = useState(parseInt(searchParams.get("radius") || "0", 10) || 0);
  const [coords, setCoords] = useState(() => getCachedCoords());
  const [locating, setLocating] = useState(false);
  const [cardGame, setCardGame] = useState(searchParams.get("card_game") || "");
  const [cardType, setCardType] = useState(searchParams.get("card_type") || "");
  const [gradedOnly, setGradedOnly] = useState(searchParams.get("graded") === "true");
  const isCards = category === TRADING_CARDS_CATEGORY;

  const pickCategory = (c) => {
    setCategory(c);
    if (c !== TRADING_CARDS_CATEGORY) {
      setCardGame(""); setCardType(""); setGradedOnly(false);
    }
  };

  const enableLocation = async () => {
    setLocating(true);
    const c = await requestCoords();
    setLocating(false);
    if (c) {
      setCoords(c);
      toast.success("Location enabled — distances and radius filters are live");
    } else {
      toast.error("Couldn't get your location");
    }
  };

  useEffect(() => {
    setLoading(true);
    const params = { q, category, delivery: delivery || undefined, sort, limit: 60 };
    if (isCards) {
      if (cardGame) params.card_game = cardGame;
      if (cardType) params.card_type = cardType;
      if (gradedOnly) params.graded = true;
    }
    if (radius > 0 && coords?.lat != null && coords?.lng != null) {
      params.lat = coords.lat;
      params.lng = coords.lng;
      params.radius_miles = radius;
    }
    api.get("/listings", { params })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    const urlParams = {};
    if (q) urlParams.q = q;
    if (category !== "All") urlParams.category = category;
    if (delivery) urlParams.delivery = delivery;
    if (sort !== "new") urlParams.sort = sort;
    if (radius > 0) urlParams.radius = String(radius);
    if (isCards && cardGame) urlParams.card_game = cardGame;
    if (isCards && cardType) urlParams.card_type = cardType;
    if (isCards && gradedOnly) urlParams.graded = "true";
    setSearchParams(urlParams, { replace: true });
  }, [q, category, delivery, sort, radius, coords?.lat, coords?.lng, cardGame, cardType, gradedOnly, isCards, setSearchParams]);

  const radiusActive = radius > 0;
  const needsLocationForRadius = radiusActive && !coords;

  return (
    <div className="section-pad py-6 md:py-10" data-testid="browse-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-4xl font-bold text-slate-900">Browse trades</h1>
        <p className="text-slate-500 mt-1.5 text-sm">{items.length} active listings — find your next swap.</p>
      </div>

      {/* Search bar */}
      <div className="flex items-stretch border border-slate-300 rounded-md overflow-hidden mb-5 focus-within:border-[#0654BA] focus-within:ring-2 focus-within:ring-[#0654BA]/15 transition">
        <Search className="w-4 h-4 text-slate-400 ml-3.5 self-center" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search items, brands, or what you want…"
          className="flex-1 bg-white outline-none px-3 py-2.5 text-sm md:text-base placeholder:text-slate-400"
          data-testid="browse-search-input"
        />
        {q && (
          <button onClick={() => setQ("")} className="px-3 text-slate-500 hover:bg-slate-100" data-testid="browse-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <p className="text-[11px] text-slate-400 -mt-3 mb-4" data-testid="boolean-search-hint">
        Pro search: combine with <span className="font-bold text-slate-500">AND</span>, <span className="font-bold text-slate-500">OR</span>, <span className="font-bold text-slate-500">NOT</span> — e.g. <span className="font-mono text-slate-500">pokemon AND graded NOT sealed</span> or <span className="font-mono text-slate-500">"psa 10"</span>
      </p>

      {/* Radius / location filter */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="browse-radius-row">
        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
        {RADIUS_OPTIONS.map(r => (
          <button
            key={r.value}
            onClick={() => setRadius(r.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md border whitespace-nowrap transition-colors flex-shrink-0 ${
              radius === r.value
                ? "bg-[#0654BA] text-white border-[#0654BA]"
                : "bg-white text-slate-700 border-slate-300 hover:border-[#0654BA] hover:text-[#0654BA]"
            }`}
            data-testid={`browse-radius-${r.value}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Location-required banner */}
      {needsLocationForRadius && (
        <button
          onClick={enableLocation}
          disabled={locating}
          className="mb-5 w-full inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-md bg-[#FFF6DE] border border-[#F5AF02] text-[#92400E] text-xs md:text-sm font-semibold hover:bg-[#FFEDB3] transition-colors disabled:opacity-60"
          data-testid="browse-enable-location"
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          {locating ? "Locating you…" : "Enable location to apply the radius filter"}
        </button>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 max-w-full" data-testid="browse-categories">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => pickCategory(c)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border whitespace-nowrap transition-colors flex-shrink-0 ${
                category === c
                  ? "bg-[#0654BA] text-white border-[#0654BA]"
                  : "bg-white text-slate-700 border-slate-300 hover:border-[#0654BA] hover:text-[#0654BA]"
              }`}
              data-testid={`browse-cat-${c.toLowerCase()}`}
            >{c}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm">
          {DELIVERY_OPTIONS.map(d => {
            const Icon = d.icon;
            return (
              <button key={d.value || "any"} onClick={() => setDelivery(d.value)} className={`px-2.5 py-1.5 text-xs font-semibold rounded-md border transition-colors flex items-center gap-1 ${delivery === d.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`} data-testid={`browse-delivery-${d.value || "any"}`}>
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {d.label}
              </button>
            );
          })}
          <Filter className="w-4 h-4 text-slate-400 ml-1" />
          <select value={sort} onChange={e => setSort(e.target.value)} className="bg-white border border-slate-300 rounded-md px-2.5 py-1.5 text-xs font-semibold text-slate-700 outline-none cursor-pointer hover:border-slate-900" data-testid="browse-sort">
            <option value="new">Newest</option>
            <option value="views">Most viewed</option>
          </select>
        </div>
      </div>

      {/* Trading Cards sub-filters */}
      {isCards && (
        <div className="mb-6 -mt-2 rounded-md border border-[#F5AF02]/40 bg-[#FFFBEB] p-3" data-testid="card-subfilters">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#92400E] flex items-center gap-1 mr-1">
              <WalletCards className="w-3.5 h-3.5" /> Cards
            </span>
            <select value={cardGame} onChange={e => setCardGame(e.target.value)} className="bg-white border border-slate-300 rounded-md px-2.5 py-1.5 text-xs font-semibold text-slate-700 outline-none cursor-pointer hover:border-[#0654BA] max-w-[200px]" data-testid="card-filter-game">
              <option value="">All games & sports</option>
              {ALL_CARD_GAMES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select value={cardType} onChange={e => setCardType(e.target.value)} className="bg-white border border-slate-300 rounded-md px-2.5 py-1.5 text-xs font-semibold text-slate-700 outline-none cursor-pointer hover:border-[#0654BA]" data-testid="card-filter-type">
              <option value="">Any type</option>
              {CARD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button
              onClick={() => setGradedOnly(!gradedOnly)}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-md border transition-colors flex items-center gap-1 ${gradedOnly ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
              data-testid="card-filter-graded"
            >
              <Award className="w-3.5 h-3.5" /> Graded only
            </button>
            {(cardGame || cardType || gradedOnly) && (
              <button onClick={() => { setCardGame(""); setCardType(""); setGradedOnly(false); }} className="text-xs text-slate-500 hover:text-rose-700 inline-flex items-center gap-1" data-testid="card-filter-clear">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="vyrti-card overflow-hidden">
              <div className="aspect-[4/3] skeleton" />
              <div className="p-3 space-y-2">
                <div className="skeleton h-3.5 w-3/4" />
                <div className="skeleton h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="vyrti-card p-12 text-center" data-testid="browse-empty">
          <p className="font-display text-xl font-bold text-slate-900 mb-2">
            {radiusActive ? `No listings within ${radius} miles` : "No listings found"}
          </p>
          <p className="text-slate-500 text-sm">
            {radiusActive ? "Try widening the radius or switching to Nationwide." : "Try a different keyword or category."}
          </p>
          {radiusActive && (
            <button onClick={() => setRadius(0)} className="mt-4 btn-block btn-accent !text-sm" data-testid="browse-clear-radius">
              Show nationwide
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4" data-testid="browse-grid">
          {items.map(l => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
}
